import React, { useState, useEffect } from 'react';
import { ref, get, update, set, push, query, orderByChild, equalTo } from 'firebase/database';
import { database } from '../firebase/config';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { pdf } from '@react-pdf/renderer';
import BollaPDF from '../pdf/BollaPDF';
import PreventivoPDF from '../pdf/PreventivoPDF';
import FatturaPDF from '../pdf/FatturaPDF';


function fmtDataIT(isoDate) {
  if (!isoDate) return '';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return isoDate;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
}

function fmtCHF(numero) {
  const n = Number(numero) || 0;
  const negativo = n < 0;
  const numeroAbs = Math.abs(n).toFixed(2);
  const [parteIntera, parteDecimale] = numeroAbs.split('.');
  const parteInteraFormattata = parteIntera.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  return (negativo ? '-' : '') + parteInteraFormattata + '.' + parteDecimale;
}

// ✅ Data odierna in formato AAAA-MM-GG calcolata in fuso orario Europe/Rome
// (stesso approccio dell'export CSV log errori: evita il "ieri" nelle prime ore del mattino)
function dataOggiRome() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

const IVA_ALIQUOTA = 0.081;


// ─────────────────────────────────────────────────────────────────────────────
// LOGICA: genera le righe candidate da una bolla (bolleForm)
// ─────────────────────────────────────────────────────────────────────────────
function generaRigheDaBolla(bolla, bollaId, veicoli) {
  const righe = [];

  (bolla.mezzi || []).forEach((mezzo) => {
    if (!mezzo.veicoloId) return;
    const veicolo = veicoli.find((v) => v.id === mezzo.veicoloId);
    const nomeVeicolo = mezzo.label || (veicolo ? `${veicolo.marca} ${veicolo.modello}` : 'Mezzo');

    const oreNum = parseFloat(mezzo.ore);
    if (oreNum > 0) {
      righe.push({
        bollaId,
        numeroBolla: bolla.numeroBolla,
        data: bolla.data,
        descrizione: `Prestazione con ${nomeVeicolo} per trasporto materiale`,
        unita: 'ore',
        quantita: oreNum,
        prezzo: veicolo?.costoOrario != null ? Number(veicolo.costoOrario) : 0,
        totale: 0, // ricalcolato dopo
        editabile: true,
      });
    }

    const kmNum = parseFloat(mezzo.km);
    if (kmNum > 0) {
      righe.push({
        bollaId,
        numeroBolla: bolla.numeroBolla,
        data: bolla.data,
        descrizione: 'Tassa traffico pesante',
        unita: 'km',
        quantita: kmNum,
        prezzo: veicolo?.costoKm != null ? Number(veicolo.costoKm) : 0,
        totale: 0,
        editabile: true,
      });
    }
  });


  const scavoNum = parseFloat(bolla.smaltimentoScavo);
  if (scavoNum > 0) {
    righe.push({
      bollaId,
      numeroBolla: bolla.numeroBolla,
      data: bolla.data,
      descrizione: 'Smaltimento materiale da scavo',
      unita: 'm3',
      quantita: scavoNum,
      prezzo: 0,
      totale: 0,
      editabile: true,
    });
  }

  const demoNum = parseFloat(bolla.smaltimentoDemo);
  if (demoNum > 0) {
    righe.push({
      bollaId,
      numeroBolla: bolla.numeroBolla,
      data: bolla.data,
      descrizione: 'Smaltimento demolizione',
      unita: 'm3',
      quantita: demoNum,
      prezzo: 0,
      totale: 0,
      editabile: true,
    });
  }

  const mistoGranulareNum = parseFloat(bolla.fornituraMistoGranulare);
  if (mistoGranulareNum > 0) {
    righe.push({
      bollaId,
      numeroBolla: bolla.numeroBolla,
      data: bolla.data,
      descrizione: 'Fornitura misto granulare 0.45',
      unita: 'm3',
      quantita: mistoGranulareNum,
      prezzo: 0,
      totale: 0,
      editabile: true,
    });
  }

  const ghiaiaNum = parseFloat(bolla.fornituraGhiaia);
  if (ghiaiaNum > 0) {
    righe.push({
      bollaId,
      numeroBolla: bolla.numeroBolla,
      data: bolla.data,
      descrizione: 'Fornitura ghiaia',
      unita: 'm3',
      quantita: ghiaiaNum,
      prezzo: 0,
      totale: 0,
      editabile: true,
    });
  }

  const asfaltoNum = parseFloat(bolla.smaltimentoAsfalto);
  if (asfaltoNum > 0) {
    righe.push({
      bollaId,
      numeroBolla: bolla.numeroBolla,
      data: bolla.data,
      descrizione: 'Smaltimento asfalto',
      unita: 'm3',
      quantita: asfaltoNum,
      prezzo: 0,
      totale: 0,
      editabile: true,
    });
  }

  righe.forEach((r) => { r.totale = (Number(r.quantita) || 0) * (Number(r.prezzo) || 0); });
  return righe;
}



const UNITA_DISPONIBILI = ['ore', 'km', 'm3', 'quintali', 'a corpo'];

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPALE
// ─────────────────────────────────────────────────────────────────────────────
export default function FatturazioneForm({ onBack, fatturaDaModificare }) {
  const oggi = new Date().toISOString().split('T')[0];

  const [clienti, setClienti] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [veicoli, setVeicoli] = useState([]);

  const [numeroFattura, setNumeroFattura] = useState(fatturaDaModificare?.numeroFattura || '');
  const [dataFattura, setDataFattura] = useState(fatturaDaModificare?.dataFattura || oggi);
  const [clienteId, setClienteId] = useState(fatturaDaModificare?.clienteId || '');
  const [cantiereId, setCantiereId] = useState(fatturaDaModificare?.cantiereId || '');

  const [righe, setRighe] = useState(fatturaDaModificare?.righe || []);
  const [inputNumeroBolla, setInputNumeroBolla] = useState('');
 // const [scontoPercento, setScontoPercento] = useState(0);
  const [modalitaPagamento, setModalitaPagamento] = useState('Bonifico 30gg D. F.');
  const [datiBancari, setDatiBancari] = useState('Raiffeisen: CH18 8080 8006 6760 6358 0');
  const [saving, setSaving] = useState(false);
  const [inputNumeroPreventivo, setInputNumeroPreventivo] = useState('');
  const [preventivoInCaricamento, setPreventivoInCaricamento] = useState(false);

  useEffect(() => {
    async function carica() {
      const [cliSnap, cantSnap, veiSnap] = await Promise.all([
        get(ref(database, 'clienti')),
        get(ref(database, 'cantieri')),
        get(ref(database, 'veicoli')),
      ]);
      if (cliSnap.exists()) setClienti(Object.entries(cliSnap.val()).map(([k, v]) => ({ id: k, ...v })));
      if (cantSnap.exists()) setCantieri(Object.entries(cantSnap.val()).map(([k, v]) => ({ id: k, ...v })));
      if (veiSnap.exists()) setVeicoli(Object.entries(veiSnap.val()).map(([k, v]) => ({ id: k, ...v })));
    }
    carica();
  }, []);

  const cantieriFiltrati = clienteId ? cantieri.filter((c) => c.clienteId === clienteId) : cantieri;

  // ── Aggiunta bolla tramite numero ────────────────────────────────────────
  async function handleAggiungiBolla() {
    const numero = inputNumeroBolla.trim();
    if (!numero) return alert('Inserisci un numero bolla');

    const snap = await get(ref(database, 'bolleForm'));
    if (!snap.exists()) return alert('Nessuna bolla trovata nel database');

    const tutte = Object.entries(snap.val()).map(([id, v]) => ({ id, ...v }));
    const bollaTrovata = tutte.find((b) => String(b.numeroBolla) === numero);

    if (!bollaTrovata) return alert(`Nessuna bolla con numero ${numero} trovata`);

    if (bollaTrovata.fatturata) {
      alert(
        `Questa bolla è già stata fatturata.\n` +
        `Fattura N°: ${bollaTrovata.numeroFatturaAssociata || '?'}\n` +
        `Data fattura: ${fmtDataIT(bollaTrovata.dataFatturaAssociata) || '?'}`
      );
      return;
    }

    const nuoveRighe = generaRigheDaBolla(bollaTrovata, bollaTrovata.id, veicoli);
    if (nuoveRighe.length === 0) {
      return alert('La bolla non contiene dati compilati (ore, km, smaltimenti) da fatturare');
    }

      // Gestione cliente e cantiere per bolle
      if (!clienteId) {
        // Primo inserimento: usa cliente della bolla
        setClienteId(bollaTrovata.clienteId);
      } else if (bollaTrovata.clienteId !== clienteId) {
        // Cliente già selezionato ma diverso da quello della bolla → blocco
        alert('La bolla appartiene a un cliente diverso da quello selezionato per la fattura.');
        return;
      }

      // Cantiere: se non ancora impostato, lo prende dalla bolla
      if (!cantiereId && bollaTrovata.cantiereId) {
        setCantiereId(bollaTrovata.cantiereId);
      }

    setRighe((prev) => [...prev, ...nuoveRighe]);
    setInputNumeroBolla('');
  }



  async function handleAggiungiPreventivo() {
    const numero = inputNumeroPreventivo.trim();
    if (!numero) return alert('Inserisci un numero di preventivo');

    setPreventivoInCaricamento(true);

    try {
      // 1. Cerca il preventivo per numero
      const preventiviRef = ref(database, 'preventivi');
      const q = query(preventiviRef, orderByChild('numeroPreventivo'), equalTo(numero));
      const snap = await get(q);

      if (!snap.exists()) {
        alert('Nessun preventivo trovato con questo numero.');
        setPreventivoInCaricamento(false);
        return;
      }

      let preventivoTrovato = null;
      let preventivoId = null;

      snap.forEach((child) => {
        preventivoId = child.key;
        preventivoTrovato = child.val();
      });

      if (!preventivoTrovato) {
        alert('Nessun preventivo trovato con questo numero.');
        setPreventivoInCaricamento(false);
        return;
      }

      // 2. Controllo: preventivo già fatturato?
      if (preventivoTrovato.fatturato) {
        alert(
          `Questo preventivo è già stato fatturato.\n` +
          `Fattura N°: ${preventivoTrovato.numeroFatturaAssociata || '?'}\n` +
          `Data fattura: ${fmtDataIT(preventivoTrovato.dataFatturaAssociata) || '?'}`
        );
        setPreventivoInCaricamento(false);
        return;
      }

      // 3. Gestione cliente (stessa logica delle bolle)
      if (!clienteId) {
        // Primo inserimento: usa cliente del preventivo
        setClienteId(preventivoTrovato.clienteId);
      } else if (preventivoTrovato.clienteId !== clienteId) {
        // Cliente già selezionato ma diverso → blocco
        alert('Il preventivo appartiene a un cliente diverso da quello selezionato per la fattura.');
        setPreventivoInCaricamento(false);
        return;
      }

      // 4. Gestione cantiere (come per le bolle)
      if (!cantiereId && preventivoTrovato.cantiereId) {
        setCantiereId(preventivoTrovato.cantiereId);
      }

      // 5. Importa le righe del preventivo nella fattura
      const nuoveRighe = (preventivoTrovato.righe || []).map((riga) => ({
        bollaId: null,
        numeroBolla: '',
        preventivoId,
        numeroPreventivo: preventivoTrovato.numeroPreventivo,
        data: preventivoTrovato.dataPreventivo,
        descrizione: riga.descrizione || '',
        unita: riga.unita || '',
        quantita: riga.quantita || 0,
        prezzo: riga.prezzo || 0,
        totale: riga.totale || 0,
        editabile: true,
        manuale: false,
        daPreventivo: true,
      }));

      if (nuoveRighe.length === 0) {
        alert('Il preventivo non contiene righe da fatturare.');
        setPreventivoInCaricamento(false);
        return;
      }

      setRighe((prev) => [...prev, ...nuoveRighe]);
      setInputNumeroPreventivo('');
    } catch (errore) {
      console.error('Errore caricamento preventivo:', errore);
      alert('Errore nel caricamento del preventivo: ' + errore.message);
    } finally {
      setPreventivoInCaricamento(false);
    }
  }

  // ── Riga manuale (es. prestazioni a corpo non legate a bolla) ───────────
function handleAggiungiRigaManuale() {
  setRighe((prev) => [
    ...prev,
    {
      bollaId: null,
      numeroBolla: '',
      data: dataOggiRome(),
      descrizione: '',
      unita: UNITA_DISPONIBILI[0],
      quantita: 0,
      prezzo: 0,
      scontoRiga: '',
      totale: 0,
      editabile: true,
      manuale: true,
    },
  ]);
}

function updateRiga(index, campo, valore) {
  setRighe((prev) => {
    const copia = [...prev];
    copia[index] = { ...copia[index], [campo]: valore };
    const qta = parseFloat(copia[index].quantita) || 0;
    const prezzo = parseFloat(copia[index].prezzo) || 0;
    const scontoRigaPercento = parseFloat(copia[index].scontoRiga) || 0;
    const totaleBase = qta * prezzo;
    copia[index].totale = totaleBase - (totaleBase * scontoRigaPercento / 100);
    return copia;
  });
}

  function rimuoviRiga(index) {
    setRighe((prev) => prev.filter((_, i) => i !== index));
  }

  async function apriPdfBolla(bollaId) {
    try {
      const bollaSnap = await get(ref(database, `bolleForm/${bollaId}`));
      if (!bollaSnap.exists()) {
        alert('Bolla non trovata.');
        return;
      }
      const bolla = bollaSnap.val();

      // Carica clienti e cantieri per risolvere i nomi
      const [cliSnap, cantSnap] = await Promise.all([
        get(ref(database, `clienti/${bolla.clienteId}`)),
        bolla.cantiereId ? get(ref(database, `cantieri/${bolla.cantiereId}`)) : Promise.resolve(null),
      ]);

      const clienteObj = cliSnap.exists() ? cliSnap.val() : null;
      const cantiereObj = cantSnap && cantSnap.exists() ? cantSnap.val() : null;

      // Costruisci oggetto "arricchito" come in RicercaBolle.js
      const bollaArricchita = {
        ...bolla,
        cliente: clienteObj?.ragioneSociale || bolla.cliente || '-',
        cantiere: cantiereObj?.luogo || bolla.cantiere || '-',
      };

      const blob = await pdf(<BollaPDF bolla={bollaArricchita} />).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('Errore apertura PDF bolla:', err);
      alert('Errore apertura PDF bolla: ' + err.message);
    }
  }

  async function apriPdfPreventivo(preventivoId) {
    try {
      const prevSnap = await get(ref(database, `preventivi/${preventivoId}`));
      if (!prevSnap.exists()) {
        alert('Preventivo non trovato.');
        return;
      }
      const preventivo = prevSnap.val();

      // Recupera cliente e cantiere per il PDF
      const [cliSnap, cantSnap] = await Promise.all([
        get(ref(database, `clienti/${preventivo.clienteId}`)),
        preventivo.cantiereId ? get(ref(database, `cantieri/${preventivo.cantiereId}`)) : Promise.resolve(null),
      ]);

      const clienteObj = cliSnap.exists() ? cliSnap.val() : null;
      const cantiereObj = cantSnap && cantSnap.exists() ? cantSnap.val() : null;

      const blob = await pdf(
        <PreventivoPDF
          preventivo={preventivo}
          cliente={clienteObj}
          cantiere={cantiereObj}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('Errore apertura PDF preventivo:', err);
      alert('Errore apertura PDF preventivo: ' + err.message);
    }
  }

  async function apriPdfFattura(fattura) {
    try {
      const [cliSnap, cantSnap] = await Promise.all([
        get(ref(database, `clienti/${fattura.clienteId}`)),
        fattura.cantiereId ? get(ref(database, `cantieri/${fattura.cantiereId}`)) : Promise.resolve(null),
      ]);

      const clienteObj = cliSnap.exists() ? cliSnap.val() : null;
      const cantiereObj = cantSnap && cantSnap.exists() ? cantSnap.val() : null;

      const blob = await pdf(
        <FatturaPDF
          fattura={fattura}
          cliente={clienteObj}
          cantiere={cantiereObj}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      console.error('Errore apertura PDF fattura:', err);
      alert('Errore apertura PDF fattura: ' + err.message);
    }
  }



  // ── Totali ────────────────────────────────────────────────────────────────
const subtotale = righe.reduce((acc, r) => acc + (Number(r.totale) || 0), 0);
const imponibile = subtotale; // Gli sconti sono già applicati a livello di riga

  const iva = imponibile * IVA_ALIQUOTA;
  const totale = imponibile + iva;

  // ── Salvataggio + PDF ────────────────────────────────────────────────────
async function handleSalva() {
  if (!numeroFattura.trim()) return alert('Inserisci il numero fattura');
  if (!clienteId) return alert('Seleziona un cliente');
  if (righe.length === 0) return alert('Aggiungi almeno una riga');

  setSaving(true);
  
  try {
    // Verifica duplicato numero fattura (salta se è la stessa in modifica)
    const fattureRef = ref(database, 'fatture');
    const q = query(fattureRef, orderByChild('numeroFattura'), equalTo(numeroFattura.trim()));
    const snap = await get(q);
    
    if (snap.exists()) {
      let duplicatoDiAltraFattura = false;
      snap.forEach((child) => {
        if (child.key !== fatturaDaModificare?.id) {
          duplicatoDiAltraFattura = true;
        }
      });
      if (duplicatoDiAltraFattura) {
        alert(`Il numero fattura "${numeroFattura}" è già in uso.`);
        return;
      }
    }

    const clienteObj = clienti.find((c) => c.id === clienteId);
    const cantiereObj = cantieri.find((c) => c.id === cantiereId);

    const fatturaPerPDF = {
      numeroFattura,
      dataFattura,
      righe,
      subtotale,
      imponibile,
      iva,
      totale,
      modalitaPagamento,
      datiBancari,
    };

    // 1. Genera PDF
    const blob = await pdf(
      <FatturaPDF
        fattura={fatturaPerPDF}
        cliente={clienteObj}
        cantiere={cantiereObj}
      />
    ).toBlob();
    
    const nomeFile = `fattura-${numeroFattura}-${clienteObj?.ragioneSociale || 'cliente'}`.replace(/\s+/g, '_') + '.pdf';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nomeFile;
    a.click();
    URL.revokeObjectURL(url);

    // 2. Salva record fattura su Firebase (creazione o modifica)
    const bolleIdUniche = [...new Set(righe.filter((r) => r.bollaId).map((r) => r.bollaId))];
    const preventiviIdUnici = [...new Set(righe.filter((r) => r.preventivoId).map((r) => r.preventivoId))];

    if (fatturaDaModificare?.id) {
      // MODIFICA: aggiorna fattura esistente
      const fatturaRecord = {
        numeroFattura,
        dataFattura,
        clienteId,
        cantiereId: cantiereId || null,
        righe,
        subtotale,
        imponibile,
        iva,
        totale,
        modalitaPagamento,
        datiBancari,
        bolleUsate: bolleIdUniche,
      };

      await update(ref(database, `fatture/${fatturaDaModificare.id}`), fatturaRecord);

      // Marca come fatturate solo le bolle NUOVAMENTE aggiunte (non già associate)
      const bolleGiaAssociate = fatturaDaModificare.bolleUsate || [];
      const bolleNuove = bolleIdUniche.filter((bollaId) => !bolleGiaAssociate.includes(bollaId));
      await Promise.all(
        bolleNuove.map((bollaId) =>
          update(ref(database, `bolleForm/${bollaId}`), {
            fatturata: true,
            numeroFatturaAssociata: numeroFattura,
            dataFatturaAssociata: dataFattura,
            fatturaIdAssociata: fatturaDaModificare.id,
          })
        )
      );

      // Marca come fatturati solo i preventivi NUOVAMENTE aggiunti
      const preventiviGiaAssociati = fatturaDaModificare.preventiviUsati || [];
      const preventiviNuovi = preventiviIdUnici.filter((p) => !preventiviGiaAssociati.includes(p));
      if (preventiviNuovi.length > 0) {
        await Promise.all(
          preventiviNuovi.map((preventivoId) =>
            update(ref(database, `preventivi/${preventivoId}`), {
              fatturato: true,
              numeroFatturaAssociata: numeroFattura,
              dataFatturaAssociata: dataFattura,
              fatturaIdAssociata: fatturaDaModificare.id,
            })
          )
        );
      }

      alert('Fattura modificata, PDF aggiornato scaricato e salvato.');
      if (onBack) onBack();
    } else {
      // CREAZIONE: nuova fattura
      const fatturaRecord = {
        numeroFattura,
        dataFattura,
        clienteId,
        cantiereId: cantiereId || null,
        righe,
        subtotale,
        imponibile,
        iva,
        totale,
        modalitaPagamento,
        datiBancari,
        bolleUsate: bolleIdUniche,
        createdAt: Date.now(),
      };

      const nuovaFatturaRef = push(ref(database, 'fatture'));
      await set(nuovaFatturaRef, fatturaRecord);

      // 3. Marca le bolle usate come fatturate (evita doppia fatturazione)
      await Promise.all(
        bolleIdUniche.map((bollaId) =>
          update(ref(database, `bolleForm/${bollaId}`), {
            fatturata: true,
            numeroFatturaAssociata: numeroFattura,
            dataFatturaAssociata: dataFattura,
            fatturaIdAssociata: nuovaFatturaRef.key,
          })
        )
      );

      // 3b. Marca i preventivi usati come fatturati
      if (preventiviIdUnici.length > 0) {
        await Promise.all(
          preventiviIdUnici.map((preventivoId) =>
            update(ref(database, `preventivi/${preventivoId}`), {
              fatturato: true,
              numeroFatturaAssociata: numeroFattura,
              dataFatturaAssociata: dataFattura,
              fatturaIdAssociata: nuovaFatturaRef.key,
            })
          )
        );
      }

      alert('Fattura generata e salvata correttamente!');
      if (onBack) onBack();
    }
    
  } catch (err) {
    console.error('Errore salvataggio fattura', err);
    alert('Errore durante il salvataggio della fattura: ' + err.message);
  } finally {
    setSaving(false);
  }
}



  // ── Render ────────────────────────────────────────────────────────────────
return (
  <div className="fatturazione-page">
    <div className="fatturazione-header">
      <button className="btn-logout" onClick={onBack}>
        Torna indietro
      </button>
    </div>

    <div className="form-bolla fatturazione-form">
        <h2>Nuova Fattura</h2>

        <div className="form-group">
          <label>Numero fattura</label>
          <input type="text" value={numeroFattura} onChange={(e) => setNumeroFattura(e.target.value)} placeholder="Es. 2026-014" />
        </div>

<div className="form-group">
  <label>Data fattura</label>
  <input 
    type="date" 
    value={dataFattura} 
    onChange={(e) => setDataFattura(e.target.value)} 
  />
</div>


        <div className="form-group">
          <label>Cliente (Spett.le)</label>
          <select value={clienteId} onChange={(e) => { setClienteId(e.target.value); setCantiereId(''); }}>
            <option value="">Seleziona cliente</option>
            {clienti.map((c) => (
              <option key={c.id} value={c.id}>{c.ragioneSociale}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Cantiere</label>
          <select value={cantiereId} onChange={(e) => setCantiereId(e.target.value)}>
            <option value="">Seleziona cantiere</option>
            {cantieriFiltrati.map((c) => (
              <option key={c.id} value={c.id}>{c.luogo}</option>
            ))}
          </select>
        </div>

        <div className="form-group" style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label>Aggiungi bolla per numero</label>
            <input
              type="text"
              value={inputNumeroBolla}
              onChange={(e) => setInputNumeroBolla(e.target.value)}
              placeholder="Es. 128"
            />
          </div>
          <button className="btn-secondary" onClick={handleAggiungiBolla}>Aggiungi</button>
        </div>

        <div className="form-group" style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label>Aggiungi preventivo per numero</label>
            <input
              type="text"
              value={inputNumeroPreventivo}
              onChange={(e) => setInputNumeroPreventivo(e.target.value)}
              placeholder="Es. PREV-2025-001"
              disabled={preventivoInCaricamento}
            />
          </div>
          <button
            className="btn-secondary"
            onClick={handleAggiungiPreventivo}
            disabled={!inputNumeroPreventivo.trim() || preventivoInCaricamento}
          >
            {preventivoInCaricamento ? 'Caricamento...' : 'Aggiungi'}
          </button>
          <button className="btn-secondary" onClick={handleAggiungiRigaManuale}>+ Riga manuale</button>
        </div>

<table className="data-table" style={{ width: '100%', marginTop: 16 }}>
  <thead>
    <tr>
      <th>N° Bolla</th>
      <th>Data</th>
      <th>Descrizione</th>
      <th>U</th>
      <th>Q.tà</th>
      <th>Prezzo CHF</th>
      <th>Sconto %</th>
      <th>Totale CHF</th>
      <th></th>
    </tr>
  </thead>
                    <tbody>
            {righe.map((r, i) => (
              <tr key={i}>
                <td>
                  {r.manuale ? (
                    <input
                      type="text"
                      value={r.numeroBolla || ''}
                      onChange={(e) => updateRiga(i, 'numeroBolla', e.target.value)}
                      placeholder="Es. Bolla 130 / Prev. XYZ"
                      style={{ width: 110 }}
                    />
                  ) : r.daPreventivo && r.preventivoId ? (
                    <button
                      type="button"
                      onClick={() => apriPdfPreventivo(r.preventivoId)}
                      title="Apri PDF preventivo"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#0066cc',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        padding: 0,
                        font: 'inherit',
                      }}
                    >
                      Prev. {r.numeroPreventivo || '-'}
                    </button>
                  ) : r.bollaId && r.numeroBolla ? (
                    <button
                      type="button"
                      onClick={() => apriPdfBolla(r.bollaId)}
                      title="Apri PDF bolla"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#0066cc',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        padding: 0,
                        font: 'inherit',
                      }}
                    >
                      Bolla {r.numeroBolla}
                    </button>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  {r.manuale ? (
                    <input
                      type="date"
                      value={r.data || ''}
                      onChange={(e) => updateRiga(i, 'data', e.target.value)}
                      style={{ width: 130 }}
                    />
                  ) : (
                    fmtDataIT(r.data)
                  )}
                </td>
                <td>
                  {r.manuale ? (
                    <input type="text" value={r.descrizione} onChange={(e) => updateRiga(i, 'descrizione', e.target.value)} />
                  ) : (
                    r.descrizione
                  )}
                </td>
                <td>
                  {r.manuale ? (
                    <select value={r.unita} onChange={(e) => updateRiga(i, 'unita', e.target.value)}>
                      {UNITA_DISPONIBILI.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  ) : (
                    r.unita
                  )}
                </td>
                <td>
                  <input type="number" value={r.quantita} onChange={(e) => updateRiga(i, 'quantita', e.target.value)} style={{ width: 70 }} />
                </td>
<td>
  <input type="number" value={r.prezzo} onChange={(e) => updateRiga(i, 'prezzo', e.target.value)} style={{ width: 80 }} />
</td>
<td>
  <input
    type="number"
    min="0"
    max="100"
    step="0.01"
    value={r.scontoRiga || ''}
    onChange={(e) => updateRiga(i, 'scontoRiga', e.target.value)}
    style={{ width: 70 }}
    placeholder=""
  />
</td>
<td>{fmtCHF(r.totale)}</td>
                <td>
                  <button className="btn-danger small" onClick={() => rimuoviRiga(i)}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

<div style={{ marginTop: 20, maxWidth: 320, marginLeft: 'auto' }}>
  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotale</span><span>CHF {fmtCHF(subtotale)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Imponibile</span><span>CHF {fmtCHF(imponibile)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>IVA 8.1%</span><span>CHF {fmtCHF(iva)}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 18, borderTop: '1px solid #333', marginTop: 6, paddingTop: 6 }}>
            <span>TOTALE</span><span>CHF {fmtCHF(totale)}</span>
          </div>
        </div>

        <div style={{ marginTop: 20, maxWidth: 320 }}>
          <div className="form-group">
            <label>Modalità di pagamento</label>
            <select value={modalitaPagamento} onChange={(e) => setModalitaPagamento(e.target.value)}>
              <option value="Bonifico 30gg D. F.">Bonifico 30gg D. F.</option>
              <option value="Vista fattura">Vista fattura</option>
            </select>
          </div>

          <div className="form-group">
            <label>Dati bancari</label>
            <select value={datiBancari} onChange={(e) => setDatiBancari(e.target.value)}>
              <option value="Raiffeisen: CH18 8080 8006 6760 63580">Raiffeisen: CH18 8080 8006 6760 6358 0</option>
              <option value="UBS: CH77 0022 6226 1745 0201X">UBS: CH77 0022 6226 1745 0201X</option>
            </select>
          </div>
        </div>

        <button className="btn-save" onClick={handleSalva} disabled={saving} style={{ marginTop: 20 }}>
          {saving ? 'Generazione fattura...' : 'Genera PDF e Salva Fattura'}
        </button>
      </div>
    </div>
  );
}
